import React from 'react';
import { Check, Lock } from 'lucide-react';
import DeviceCollage from './DeviceCollage';
import AiChatMock from './AiChatMock';

export function EnterpriseBadge({ d, onClick }) {
  const style = {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 11px', borderRadius: 20,
    background: d.primary, color: '#fff', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', border: 'none', cursor: onClick ? 'pointer' : 'default',
  };
  const inner = <><Lock size={11} /> Enterprise</>;
  return onClick
    ? <button type="button" style={style} onClick={onClick}>{inner}</button>
    : <span style={style}>{inner}</span>;
}

// Alternating "text | device collage" rows. Each module is { id, eyebrow, title, blurb, bullets,
// enterprise, visual?, shot } (see src/data/marketingModules.js). A module with visual: 'chat' also
// floats the sample WhatsApp conversation over the collage.
export default function ModuleShowcase({ d, isDark, isMobile, modules, onContact, ctaLabel = 'Talk to us about this' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 56 : 88 }}>
      {modules.map((m, idx) => {
        const reverse = !isMobile && idx % 2 === 1;

        const text = (
          <div style={{ order: reverse ? 2 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
              <span style={{ display: 'inline-block', background: d.chipBg, color: d.chipText, padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {m.eyebrow}
              </span>
              {m.enterprise && <EnterpriseBadge d={d} />}
            </div>
            <h3 style={{ fontSize: isMobile ? 26 : 'clamp(26px,3vw,36px)', fontWeight: 700, lineHeight: 1.2, margin: '0 0 14px', color: d.text }}>
              {m.title}
            </h3>
            <p style={{ fontSize: 16, color: d.textSec, lineHeight: 1.75, margin: '0 0 22px' }}>{m.blurb}</p>
            <ul style={{ listStyle: 'none', margin: '0 0 26px', padding: 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
              {m.bullets.map((b) => (
                <li key={b} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', fontSize: 14.5, color: d.textSec, lineHeight: 1.55 }}>
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: d.primaryCont, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <Check size={12} color={d.primary} strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
            {onContact && (
              <button
                type="button"
                onClick={onContact}
                style={{ background: 'transparent', color: d.primary, border: `2px solid ${d.primary}`, borderRadius: 28, padding: '10px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              >
                {ctaLabel}
              </button>
            )}
          </div>
        );

        const chat = m.visual === 'chat' && (
          <div style={{ position: 'absolute', top: isMobile ? '-4%' : '-9%', right: isMobile ? '-2%' : '-3%', width: isMobile ? '56%' : '44%', zIndex: 3 }}>
            <AiChatMock d={d} isDark={isDark} compact />
          </div>
        );

        const visual = (
          <div style={{ order: reverse ? 1 : 2, position: 'relative' }}>
            <DeviceCollage d={d} {...m.shot} overlay={chat || null} />
          </div>
        );

        return (
          <div
            key={m.id}
            id={m.id}
            style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.1fr', gap: isMobile ? 28 : 64, alignItems: 'center' }}
          >
            {text}
            {visual}
          </div>
        );
      })}
    </div>
  );
}
